import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { DiaryManagementNavTabs } from "@/features/admin/components/DiaryManagementNavTabs";
import {
    ADMIN_TABLE_PAGE_SIZE,
    AdminColumnsDialog,
    AdminTablePagination,
    useDebouncedValue,
} from "@/features/admin/tables";
import {
    ADMIN_TEMPLATE_LINK_STATUSES,
    useAdminTemplates,
    type AdminTemplateFilters,
} from "@/features/admin/templates/api/adminTemplatesApi";
import {
    ADMIN_TEMPLATES_COLUMNS,
    AdminTemplatesTable,
} from "@/features/admin/templates/components/AdminTemplatesTable";
import {
    AdminTemplatesToolbar,
    emptyAdminTemplatesFilterValues,
    type AdminTemplatesFilterValues,
} from "@/features/admin/templates/components/AdminTemplatesToolbar";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

const TemplatesSearchZ = z.object({
    q: z.string().optional(),
    page: z.coerce.number().int().min(1).optional(),
    ownerId: z.string().optional(),
    masterId: z.string().optional(),
    studyId: z.string().optional(),
    linkStatus: z.enum(ADMIN_TEMPLATE_LINK_STATUSES).optional(),
    visibility: z.enum(["public", "private"]).optional(),
    clinicianIds: z.array(z.string()).optional(),
    labels: z.array(z.string()).optional(),
    organisationIds: z.array(z.string()).optional(),
    formIds: z.array(z.string()).optional(),
    eventTypes: z.array(z.string()).optional(),
});

const ARRAY_FILTER_KEYS = ["clinicianIds", "labels", "organisationIds", "formIds", "eventTypes"] as const;

const emptyToUndefined = (values: string[]) => (values.length > 0 ? values : undefined);

export const Route = createFileRoute("/_app/admin/templates")({
    component: AdminTemplatesPage,
    validateSearch: (search) => TemplatesSearchZ.parse(search),
});

function AdminTemplatesPage() {
    useNav([
        { label: "Admin", route: { to: "/admin" } },
        { label: "Templates", route: { to: "/admin/templates" } },
    ]);

    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    const [searchText, setSearchText] = useState(search.q ?? "");
    const debouncedSearch = useDebouncedValue(searchText.trim(), 300);

    const filterValues: AdminTemplatesFilterValues = useMemo(
        () => ({
            ...emptyAdminTemplatesFilterValues,
            ownerId: search.ownerId ?? null,
            masterId: search.masterId ?? null,
            studyId: search.studyId ?? null,
            linkStatus: search.linkStatus ?? "all",
            visibility: search.visibility ?? "all",
            clinicianIds: search.clinicianIds ?? [],
            labels: search.labels ?? [],
            organisationIds: search.organisationIds ?? [],
            formIds: search.formIds ?? [],
            eventTypes: search.eventTypes ?? [],
        }),
        [search]
    );

    const [visibleKeys, setVisibleKeys] = useState(() =>
        ADMIN_TEMPLATES_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)
    );

    const page = search.page ?? 1;

    useEffect(() => {
        const urlQ = search.q ?? "";
        if (debouncedSearch === urlQ) return;
        void navigate({
            search: (prev) => ({ ...prev, q: debouncedSearch || undefined, page: 1 }),
            replace: true,
        });
    }, [debouncedSearch, navigate, search.q]);

    const filters: AdminTemplateFilters = useMemo(
        () => ({
            q: debouncedSearch || undefined,
            ownerId: filterValues.ownerId ?? undefined,
            masterId: filterValues.masterId ?? undefined,
            studyId: filterValues.studyId ?? undefined,
            linkStatus: filterValues.linkStatus === "all" ? undefined : filterValues.linkStatus,
            isPublic: filterValues.visibility === "all" ? undefined : filterValues.visibility === "public",
            ...Object.fromEntries(ARRAY_FILTER_KEYS.map((key) => [key, emptyToUndefined(filterValues[key])])),
            page,
            pageSize: ADMIN_TABLE_PAGE_SIZE,
        }),
        [debouncedSearch, filterValues, page]
    );

    const { data, isLoading, isFetching, error } = useAdminTemplates(filters);
    const templates = data?.templates ?? [];

    return (
        <AdminPageLayout
            title="Templates"
            description="Browse every template across owners and set which master template each one follows."
        >
            <div className="space-y-4">
                <DiaryManagementNavTabs
                    actions={
                        <AdminTemplatesToolbar
                            searchText={searchText}
                            onSearchTextChange={setSearchText}
                            filterValues={filterValues}
                            onApplyFilters={(values) => {
                                void navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        page: 1,
                                        ownerId: values.ownerId ?? undefined,
                                        masterId: values.masterId ?? undefined,
                                        studyId: values.studyId ?? undefined,
                                        linkStatus: values.linkStatus === "all" ? undefined : values.linkStatus,
                                        visibility: values.visibility === "all" ? undefined : values.visibility,
                                        ...Object.fromEntries(
                                            ARRAY_FILTER_KEYS.map((key) => [key, emptyToUndefined(values[key])])
                                        ),
                                    }),
                                    replace: true,
                                });
                            }}
                            isApplying={isFetching}
                        >
                            <AdminColumnsDialog
                                columns={ADMIN_TEMPLATES_COLUMNS}
                                visibleKeys={visibleKeys}
                                onSave={setVisibleKeys}
                            />
                        </AdminTemplatesToolbar>
                    }
                />

                {error && (
                    <div className="bg-red-50 p-4 border border-red-200 rounded-md text-red-800 text-sm">
                        Error loading templates: {error instanceof Error ? error.message : "Unknown error"}
                    </div>
                )}

                {isLoading ? (
                    <ui.Skeleton className="w-full h-64" />
                ) : (
                    !error && (
                        <div className={isFetching ? "opacity-70 transition-opacity" : undefined}>
                            <AdminTemplatesTable templates={templates} visibleKeys={visibleKeys} />
                            <div className="mt-3">
                                <AdminTablePagination
                                    page={page}
                                    totalCount={data?.totalCount ?? 0}
                                    pageSize={ADMIN_TABLE_PAGE_SIZE}
                                    isFetching={isFetching}
                                    onPageChange={(nextPage) => {
                                        void navigate({
                                            search: (prev) => ({ ...prev, page: nextPage }),
                                            replace: true,
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    )
                )}
            </div>
        </AdminPageLayout>
    );
}
